'use client';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { driver, type Driver } from 'driver.js';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { USER_ROLE } from '~/shared/constants';
import {
  keepOnboardingTargetVisible,
  makeOnboardingStepsViewportSafe,
} from '~/shared/helpers/keep-onboarding-target-visible';
import { restorePlatformScrollAfterTour } from '~/shared/helpers/restore-platform-scroll-after-tour';
import { useTypedTranslation } from '~/shared/hooks';
import { graphqlApi } from '~/shared/store/api-services/graphql-api';
import { useAppDispatch, useAppSelector } from '~/shared/store/hooks';
import { getAppUser } from '~/shared/store/slices/app/selectors';

import 'driver.js/dist/driver.css';
import './platform-page-onboarding.scss';

type PageKey =
  | 'courseList'
  | 'courseModules'
  | 'courseBoard'
  | 'studentsList'
  | 'studentsBoard'
  | 'calendar'
  | 'teachers'
  | 'managers';

type PageConfig = Readonly<{
  key: PageKey;
  scope: string;
  route: RegExp;
  buttonTarget: string;
  showButton?: boolean;
  roles: readonly string[] | 'except-manager';
  steps: readonly { selector?: string; title: string; description: string; side: 'top' | 'bottom' | 'left' | 'right' }[];
}>;

const PAGE_CONFIGS: readonly PageConfig[] = [
  {
    key: 'courseList',
    scope: 'platform.courseList',
    route: /\/platform\/(?:online-courses|video-courses)\/?$/,
    buttonTarget: '',
    showButton: false,
    roles: [USER_ROLE.STUDENT, USER_ROLE.TEACHER, USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '[data-course-onboarding="tabs"]', title: 'text.course-onboarding-tabs-title', description: 'text.course-onboarding-tabs-description', side: 'bottom' },
      { selector: '[data-course-onboarding="filters"]', title: 'text.course-onboarding-filters-title', description: 'text.course-onboarding-filters-description', side: 'bottom' },
      { title: 'text.course-onboarding-list-title', description: 'text.course-onboarding-list-description', side: 'top' },
    ],
  },
  {
    key: 'courseModules',
    scope: 'platform.courseModules',
    route: /\/platform\/(?:online-courses|video-courses)\/[^/]+\/categories\/?$/,
    buttonTarget: '',
    showButton: false,
    roles: [USER_ROLE.STUDENT, USER_ROLE.TEACHER, USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.online-course-categories__container', title: 'text.course-onboarding-modules-title', description: 'text.course-onboarding-modules-description', side: 'top' },
      { selector: '.course-categories-list', title: 'text.course-onboarding-module-card-title', description: 'text.course-onboarding-module-card-description', side: 'top' },
    ],
  },
  {
    key: 'courseBoard',
    scope: 'platform.courseBoardV3',
    route: /\/platform\/(?:online-courses\/[^/]+\/[^/]+\/board|video-courses\/[^/]+\/[^/]+\/player)\/?$/,
    buttonTarget: '',
    showButton: false,
    roles: [USER_ROLE.STUDENT, USER_ROLE.TEACHER, USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.online-course-tools, .navigation-tools', title: 'text.course-onboarding-board-navigation-title', description: 'text.course-onboarding-board-navigation-description', side: 'bottom' },
      { selector: '.user-info-card__button-item--start-horizontal-mode', title: 'text.course-onboarding-board-nearest-lesson-title', description: 'text.course-onboarding-board-nearest-lesson-description', side: 'bottom' },
      { selector: '.online-course-board__container', title: 'text.course-onboarding-board-title', description: 'text.course-onboarding-board-description', side: 'top' },
      { selector: '.lessons-list > .accent-header', title: 'text.course-onboarding-board-cards-title', description: 'text.course-onboarding-board-cards-description', side: 'bottom' },
      { selector: '.lesson-card', title: 'text.course-onboarding-board-card-title', description: 'text.course-onboarding-board-card-description', side: 'right' },
      { selector: '.lesson-card__footer-actions', title: 'text.course-onboarding-board-actions-title', description: 'text.course-onboarding-board-actions-description', side: 'top' },
      { selector: '.lesson-card__details-button', title: 'text.course-onboarding-board-details-title', description: 'text.course-onboarding-board-details-description', side: 'right' },
      { selector: '.lesson-card__home-task-button', title: 'text.course-onboarding-board-homework-title', description: 'text.course-onboarding-board-homework-description', side: 'top' },
      { selector: '.lesson-card__start-button-wrapper', title: 'text.course-onboarding-board-open-title', description: 'text.course-onboarding-board-open-description', side: 'top' },
      { selector: '.online-course-board__tabs', title: 'text.course-onboarding-board-tools-title', description: 'text.course-onboarding-board-tools-description', side: 'left' },
    ],
  },
  {
    key: 'studentsList',
    scope: 'platform.studentsList',
    route: /\/platform\/students\/list\/?$/,
    buttonTarget: '.students-list__page-tabs-row',
    roles: [USER_ROLE.STUDENT, USER_ROLE.MANAGER, USER_ROLE.TEACHER, USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.students-list__page-tabs-row', title: 'text.page-onboarding-students-navigation-title', description: 'text.page-onboarding-students-navigation-description', side: 'bottom' },
      { selector: '.students-list__actions-container', title: 'text.page-onboarding-students-filters-title', description: 'text.page-onboarding-students-filters-description', side: 'bottom' },
      { selector: '.students-list__table', title: 'text.page-onboarding-students-table-title', description: 'text.page-onboarding-students-table-description', side: 'top' },
    ],
  },
  {
    key: 'studentsBoard',
    scope: 'platform.studentsBoard',
    route: /\/platform\/students\/board\/?$/,
    buttonTarget: '.students-board__page-tabs-row, .students-list__page-tabs-row',
    roles: [USER_ROLE.STUDENT, USER_ROLE.MANAGER, USER_ROLE.TEACHER, USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.students-board__page-tabs-row, .students-list__page-tabs-row', title: 'text.page-onboarding-board-navigation-title', description: 'text.page-onboarding-board-navigation-description', side: 'bottom' },
      { selector: '.students-board__tools, .students-list__actions-container', title: 'text.page-onboarding-board-filters-title', description: 'text.page-onboarding-board-filters-description', side: 'bottom' },
      { selector: '.students-board__content-container, .students-list__content', title: 'text.page-onboarding-board-cards-title', description: 'text.page-onboarding-board-cards-description', side: 'top' },
    ],
  },
  {
    key: 'calendar',
    scope: 'platform.calendar',
    route: /\/platform\/calendar\/?$/,
    buttonTarget: '.calendar__actions-block',
    roles: 'except-manager',
    steps: [
      { selector: '.calendar__title', title: 'text.page-onboarding-calendar-title', description: 'text.page-onboarding-calendar-description', side: 'bottom' },
      { selector: '.calendar__actions-block', title: 'text.page-onboarding-calendar-filters-title', description: 'text.page-onboarding-calendar-filters-description', side: 'bottom' },
      { selector: '.calendar__calendar-container', title: 'text.page-onboarding-calendar-grid-title', description: 'text.page-onboarding-calendar-grid-description', side: 'top' },
    ],
  },
  {
    key: 'teachers',
    scope: 'platform.teachers',
    route: /\/platform\/teachers\/?$/,
    buttonTarget: '.teachers .items-editor__tools-container',
    roles: [USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.teachers__page-heading', title: 'text.page-onboarding-teachers-title', description: 'text.page-onboarding-teachers-description', side: 'bottom' },
      { selector: '.teachers__course-panel', title: 'text.page-onboarding-people-filter-title', description: 'text.page-onboarding-people-filter-description', side: 'bottom' },
      { selector: '.teachers .items-editor__table-container', title: 'text.page-onboarding-teachers-table-title', description: 'text.page-onboarding-teachers-table-description', side: 'top' },
    ],
  },
  {
    key: 'managers',
    scope: 'platform.managers',
    route: /\/platform\/managers\/?$/,
    buttonTarget: '.managers .items-editor__tools-container',
    roles: [USER_ROLE.SUPER_TEACHER],
    steps: [
      { selector: '.managers__page-heading', title: 'text.page-onboarding-managers-title', description: 'text.page-onboarding-managers-description', side: 'bottom' },
      { selector: '.managers__course-panel', title: 'text.page-onboarding-people-filter-title', description: 'text.page-onboarding-people-filter-description', side: 'bottom' },
      { selector: '.managers .items-editor__table-container', title: 'text.page-onboarding-managers-table-title', description: 'text.page-onboarding-managers-table-description', side: 'top' },
    ],
  },
];

const parseStatus = (value?: string | null) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

const PlatformPageOnboarding = () => {
  const pathname = usePathname();
  const user = useAppSelector(getAppUser);
  const dispatch = useAppDispatch();
  const t = useTypedTranslation(['text']);
  const driverRef = useRef<Driver | null>(null);
  const startedKeyRef = useRef<string | null>(null);
  const lastActiveIndexRef = useRef(0);
  const isDismissingRef = useRef(false);
  const startRef = useRef<(requestedStep?: number) => boolean>(() => false);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    const isClickOutsideDriverPopover = (event: Event) => {
      const hasActiveTour = Boolean(document.querySelector('.driverjs-course-onboarding-skip'));
      if (!hasActiveTour) return false;

      return !event.composedPath().some((target) => {
        return target instanceof Element && target.classList.contains('driver-popover');
      });
    };

    const triggerGlobalSkip = (event: Event) => {
      if (!isClickOutsideDriverPopover(event)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const skipButton = document.querySelector<HTMLButtonElement>('.driverjs-course-onboarding-skip');
      if (!skipButton) return;

      // This synthetic click belongs to the popover, so the window capture
      // handler lets it reach the real Skip listener.
      skipButton.click();
    };

    // Capture the click before Driver.js or the highlighted page control can
    // handle it. The synthetic Skip click is inside the popover and passes.
    window.addEventListener('click', triggerGlobalSkip, true);

    return () => {
      window.removeEventListener('click', triggerGlobalSkip, true);
    };
  }, []);

  const config = useMemo(() => PAGE_CONFIGS.find((item) => item.route.test(pathname ?? '')) ?? null, [pathname]);
  const isRoleAllowed = Boolean(
    config &&
      user?.role &&
      (config.roles === 'except-manager'
        ? user.role !== USER_ROLE.MANAGER
        : config.roles.includes(user.role)),
  );

  const saveStatus = useCallback(
    async (values: { completed?: boolean; dismissed?: boolean; lastStep?: number }) => {
      if (!config || !isRoleAllowed) return;
      await dispatch(
        graphqlApi.endpoints.updateCourseIntroStatusUser.initiate({
          scope: config.scope,
          ...values,
          hideFlashMessage: true,
        }),
      ).unwrap();
    },
    [config, dispatch, isRoleAllowed],
  );

  const start = useCallback(
    (requestedStep = 0) => {
      if (!config || !isRoleAllowed || typeof document === 'undefined') return false;

      if (config.key === 'courseBoard') {
        const isLoading = Boolean(document.querySelector('.online-course-board__spinner'));
        const isBoardContentReady = Boolean(
          document.querySelector(
            '.lesson-card, .online-course-board__module-ready, .lessons-list__empty-state-banner, .lessons-list__no-data-wrapper',
          ),
        );
        const isStudentTopPanelReady =
          user?.role !== USER_ROLE.STUDENT ||
          Boolean(document.querySelector('.user-info-card--horizontal-mode, .online-course-tools__user-info'));

        if (isLoading || !isBoardContentReady || !isStudentTopPanelReady) return false;
      }

      const steps = makeOnboardingStepsViewportSafe(
        config.steps
          .filter((step) => !step.selector || document.querySelector(step.selector))
          .map((step) => ({
            element: step.selector,
            popover: {
              title: t(step.title),
              description: t(step.description),
              side: step.side,
            },
          })),
      );

      if (!steps.length) return false;

      driverRef.current?.destroy();
      const instance = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        disableActiveInteraction: false,
        stagePadding: 7,
        stageRadius: 8,
        popoverOffset: 12,
        nextBtnText: t('text.intro-next'),
        prevBtnText: t('text.intro-prev'),
        doneBtnText: t('text.intro-done'),
        steps,
        onPopoverRender: (popover, { state, driver: activeDriver }) => {
          const footer = popover.footerButtons;
          if (!footer || footer.querySelector('.driverjs-course-onboarding-skip')) return;

          const skipButton = document.createElement('button');
          skipButton.type = 'button';
          skipButton.className = 'driverjs-course-onboarding-skip';
          skipButton.textContent = t('text.intro-skip');
          skipButton.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (isDismissingRef.current) return;

            isDismissingRef.current = true;
            const activeIndex = state.activeIndex ?? lastActiveIndexRef.current;
            try {
              await saveStatus({ completed: false, dismissed: true, lastStep: activeIndex });
              activeDriver.destroy();
            } finally {
              isDismissingRef.current = false;
            }
          });
          footer.appendChild(skipButton);
        },
        onHighlighted: (element, _step, { state }) => {
          keepOnboardingTargetVisible(element);

          const activeIndex = state.activeIndex ?? 0;
          lastActiveIndexRef.current = activeIndex;
          void saveStatus({ lastStep: activeIndex });
        },
        onNextClick: async (_element, _step, { state, driver: activeDriver }) => {
          const activeIndex = state.activeIndex ?? 0;
          if (activeIndex >= steps.length - 1) {
            await saveStatus({ completed: true, dismissed: false, lastStep: activeIndex });
            activeDriver.destroy();
            return;
          }
          activeDriver.moveNext();
        },
        onCloseClick: async (_element, _step, { state, driver: activeDriver }) => {
          if (isDismissingRef.current) return;

          isDismissingRef.current = true;
          const activeIndex = state.activeIndex ?? lastActiveIndexRef.current;
          try {
            await saveStatus({ completed: false, dismissed: true, lastStep: activeIndex });
            activeDriver.destroy();
          } finally {
            isDismissingRef.current = false;
          }
        },
        onDestroyed: () => {
          driverRef.current = null;
          restorePlatformScrollAfterTour();
        },
      });

      driverRef.current = instance;
      instance.drive(Math.min(Math.max(requestedStep, 0), steps.length - 1));
      return true;
    },
    [config, isRoleAllowed, saveStatus, t, user?.role],
  );

  const replay = useCallback(async () => {
    if (!config) return;
    await saveStatus({ completed: false, dismissed: false, lastStep: 0 });
    start(0);
  }, [config, saveStatus, start]);

  startRef.current = start;

  useEffect(() => {
    setPortalTarget(null);
    if (!config || !isRoleAllowed || config.showButton === false || !config.buttonTarget) return;

    const updateTarget = () => setPortalTarget(document.querySelector(config.buttonTarget));
    updateTarget();
    const observer = new MutationObserver(updateTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [config, isRoleAllowed]);

  useEffect(() => {
    if (!config || !isRoleAllowed || !user?._id) return;
    const startKey = `${user._id}:${config.key}`;
    if (startedKeyRef.current === startKey) return;
    let cancelled = false;
    let observer: MutationObserver | null = null;
    let retryTimer: number | null = null;

    void (async () => {
      const result = await dispatch(
        graphqlApi.endpoints.updateCourseIntroStatusUser.initiate({
          scope: config.scope,
          hideFlashMessage: true,
        }),
      );
      const message =
        result?.data?.data?.updateCourseIntroStatusUserMutation?.message ??
        result?.data?.updateCourseIntroStatusUserMutation?.message ??
        null;
      const status = parseStatus(message);
      if (cancelled || status.completed || status.dismissed) return;

      const tryStart = () => {
        if (cancelled) return;
        if (startRef.current(Number(status.lastStep) || 0)) {
          startedKeyRef.current = startKey;
          observer?.disconnect();
          observer = null;
        }
      };

      // Some platform pages finish rendering only after their data queries resolve.
      // Keep waiting for the actual targets instead of abandoning auto-start after
      // a short polling window.
      observer = new MutationObserver(tryStart);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      retryTimer = window.setTimeout(tryStart, 250);
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [config, dispatch, isRoleAllowed, user?._id]);

  if (!config || !isRoleAllowed || config.showButton === false || !portalTarget) return null;

  return createPortal(
    <Tooltip title={t('text.course-onboarding-replay')}>
      <Button
        type="default"
        htmlType="button"
        size="large"
        className={`platform-page-onboarding__button ${
          config.key === 'teachers' || config.key === 'managers'
            ? 'platform-page-onboarding__button--people'
            : ''
        }`}
        icon={<QuestionCircleOutlined />}
        aria-label={t('text.course-onboarding-replay')}
        onClick={replay}
      />
    </Tooltip>,
    portalTarget,
  );
};

export default PlatformPageOnboarding;
